/**
 * The team setup routes the console reads (ADR-217 with Amendments 1 and 2),
 * typed from specs/changes/team-setup/openapi.yaml. The console publishes
 * nothing here; the one write is the list of skills in the team setup.
 */
import axios from 'axios';
import { SYSTEMDOX_API_URL } from '@/lib/apiConfig';

const api = axios.create({ baseURL: SYSTEMDOX_API_URL });

export type Part = 'hooks' | 'mcp' | 'agents' | 'commands' | 'instructions';

export interface FileMeta {
  path: string;
  bytes: number;
  sha256: string;
}

export interface FileOut extends FileMeta {
  content: string;
}

export interface PartVersionSummary {
  part: Part;
  version: number;
  publishedAt: string;
  /** User id of the publisher. */
  publishedBy: string;
  files: FileMeta[];
}

export interface PartDetail {
  part: Part;
  latestVersion: number;
  /** Newest first, at most 20. */
  versions: PartVersionSummary[];
}

export interface PartVersionFiles extends Omit<PartVersionSummary, 'files'> {
  files: FileOut[];
}

export interface McpServerLine {
  name: string;
  command?: string;
  args?: string[];
  url?: string;
}

export interface PluginVersion {
  version: number;
  sha256: string;
  bytes: number;
  createdAt: string;
  createdBy?: string;
  parts: Record<string, number>;
  skills: Record<string, number>;
  /** Every hook `command` verbatim. */
  hookCommands?: string[];
  /** Every MCP server with its `command` and `args`, or its `url`, verbatim. */
  mcpServers?: McpServerLine[];
  command: string;
}

export interface PluginHistory {
  name: string;
  /** Newest first, at most 20. */
  versions: PluginVersion[];
}

export interface MemoryProjectSummary {
  project: string;
  latestVersion: number;
  fileCount: number;
  bytes: number;
  updatedAt: string;
  spaceId: string;
}

export interface TeamSetup {
  workspace: string;
  minClaudeCodeVersion: string;
  marketplaceUrl: string;
  plugin?: PluginVersion;
  parts: Record<string, number>;
  skills: string[];
  memory: MemoryProjectSummary[];
  /** Absent on a service older than ADR-223. */
  sessionBrief?: SessionBrief;
}

/** What every session with the SystemDox MCP server is told (ADR-223). */
export interface SessionBrief {
  text: string;
  skills: { name: string; description: string; latestVersion: number }[];
  heldSkills: { name: string; reason: string }[];
  omittedSkills: number;
}

export interface MemoryFile {
  sha256: string;
  bytes: number;
  /** User id whose push brought this content. */
  syncedBy: string;
  syncedAt: string;
}

export interface MemoryVersion {
  project: string;
  version: number;
  createdAt: string;
  createdBy?: string;
  files: Record<string, MemoryFile>;
}

export type ValueKind = 'phone' | 'email';

/** One answered value. There is no value and no HMAC in it. */
export interface ValueAnswerListed {
  kind: ValueKind;
  answer: 'ours' | 'member' | 'person';
  personId?: string;
  label?: string;
  /** User id. */
  answeredBy: string;
  answeredAt: string;
}

/** The SYD code of a refused call (problem+json `code`), or null. */
export function refusalCode(err: unknown): string | null {
  const data = (err as { response?: { data?: { code?: unknown } } })?.response?.data;
  return typeof data?.code === 'string' ? data.code : null;
}

/** The refusal's `detail`, which starts with its code, or null. */
export function refusalDetail(err: unknown): string | null {
  const data = (err as { response?: { data?: { detail?: unknown } } })?.response?.data;
  return typeof data?.detail === 'string' ? data.detail : null;
}

const ws = (workspace: string) => `/team-setup/${encodeURIComponent(workspace)}`;

export const teamSetupApi = {
  setAuthProvider(getToken: () => Promise<string | null>) {
    api.interceptors.request.use(async (config) => {
      const token = await getToken();
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    });
  },

  /** getOwnTeamSetup: the caller's own workspace, learnt from the token alone. */
  async getOwn(): Promise<TeamSetup> {
    const { data } = await api.get('/team-setup');
    return data;
  },

  /** setSetupSkills: replace the whole list of skills in the team setup. */
  async setSkills(workspace: string, skills: string[]): Promise<string[]> {
    const { data } = await api.put(`${ws(workspace)}/skills`, { skills });
    return data.skills;
  },

  /** getPlugin: the team plugin's versions, newest first. */
  async getPlugin(workspace: string): Promise<PluginHistory> {
    const { data } = await api.get(`${ws(workspace)}/plugin`);
    return data;
  },

  /** getPart: one part with its version history (no file content). */
  async getPart(workspace: string, part: Part): Promise<PartDetail> {
    const { data } = await api.get(`${ws(workspace)}/parts/${part}`);
    return data;
  },

  /** getPartVersion: one version's files, with content. */
  async getPartVersion(workspace: string, part: Part, version: number | 'latest'): Promise<PartVersionFiles> {
    const { data } = await api.get(`${ws(workspace)}/parts/${part}/versions/${version}`);
    return data;
  },

  /** listMemoryProjects: the team memories the caller can read. */
  async listMemoryProjects(workspace: string): Promise<MemoryProjectSummary[]> {
    const { data } = await api.get(`${ws(workspace)}/memory`);
    return data.projects;
  },

  /** getMemory: the latest version's files, with who synced each and when. */
  async getMemory(workspace: string, project: string): Promise<MemoryVersion> {
    const { data } = await api.get(`${ws(workspace)}/memory/${encodeURIComponent(project)}`);
    return data;
  },

  /** listValueAnswers: the workspace's answered values, never a value. */
  async listValueAnswers(workspace: string): Promise<ValueAnswerListed[]> {
    const { data } = await api.get(`${ws(workspace)}/values`);
    return data.answers;
  },
};
